/**
 * FormTextField Component
 * 
 * Standardized text field component for forms using MUI TextField.
 * 
 * @example
 * <FormTextField
 *   name="email"
 *   label="Email"
 *   value={email}
 *   onChange={(e) => setEmail(e.target.value)}
 *   required
 * />
 */

import React from 'react';
import {
  TextField,
  TextFieldProps,
  InputAdornment,
} from '@mui/material';

export interface FormTextFieldProps extends Omit<TextFieldProps, 'variant'> {
  /** Field name */
  name: string;
  /** Field label */
  label: string;
  /** Error message */
  errorMessage?: string;
  /** Helper text (shown when no error) */
  helperText?: string;
  /** Start icon/adornment */
  startIcon?: React.ReactNode;
  /** End icon/adornment */
  endIcon?: React.ReactNode;
  /** Variant style */
  variant?: 'outlined' | 'filled' | 'standard';
}

export const FormTextField = React.forwardRef<
  HTMLInputElement,
  FormTextFieldProps
>(({
    name,
    label,
    errorMessage,
    helperText,
    startIcon,
    endIcon,
    variant = 'outlined',
    inputRef,
    ...props
  }, forwardedRef) => {
  const hasError = Boolean(errorMessage);

  return (
    <TextField
      inputRef={inputRef ?? forwardedRef}
      name={name}
      label={label}
      variant={variant}
      fullWidth
      error={hasError}
      helperText={hasError ? errorMessage : helperText}
      InputProps={{
        startAdornment: startIcon ? (
          <InputAdornment position="start">{startIcon}</InputAdornment>
        ) : undefined,
        endAdornment: endIcon ? (
          <InputAdornment position="end">{endIcon}</InputAdornment>
        ) : undefined,
      }}
      sx={{
        '& .MuiOutlinedInput-root': {
          borderRadius: 2,
        },
      }}
      {...props}
    />
  );
});

FormTextField.displayName = 'FormTextField';

export default FormTextField;
